// src/components/ScoreInput.js
import React, { useState } from "react";
import { useAuth } from "./AuthProvider";
import { firestore, timestamp } from "../firebase";
import { collection, addDoc } from "firebase/firestore";
import confetti from 'canvas-confetti';
import './ScoreInput.css';

export default function ScoreInput() {
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [frames, setFrames] = useState(
    Array.from({ length: 10 }, () => ({ roll1: "", roll2: "", roll3: "" }))
  );
  const [currentPos, setCurrentPos] = useState([0, 0]);

  const computeTotals = (framesArr) => {
    const totals = [];
    for (let i = 0; i < framesArr.length; i++) {
      let score = 0;
      for (let j = 0; j <= i; j++) {
        const r1 = parseInt(framesArr[j].roll1, 10) || 0;
        const r2 = parseInt(framesArr[j].roll2, 10) || 0;
        const r3 = parseInt(framesArr[j].roll3, 10) || 0;
        if (j < 9) {
          if (r1 === 10) {
            const n1 = parseInt(framesArr[j + 1]?.roll1, 10) || 0;
            let n2;
            if (j === 8) n2 = parseInt(framesArr[9].roll2, 10) || 0;
            else if (n1 === 10) n2 = parseInt(framesArr[j + 2]?.roll1, 10) || 0;
            else n2 = parseInt(framesArr[j + 1]?.roll2, 10) || 0;
            score += 10 + n1 + n2;
          } else if (r1 + r2 === 10) {
            const n1 = parseInt(framesArr[j + 1]?.roll1, 10) || 0;
            score += 10 + n1;
          } else { score += r1 + r2; }
        } else { score += r1 + r2 + r3; }
      }
      totals.push(score);
    }
    return totals;
  };

  const runningTotals = computeTotals(frames);

  const handlePinClick = (pin) => {
    const [f, r] = currentPos;
    const newFrames = [...frames];
    let nf = f, nr = r;

    if (f < 9) {
      if (r === 0) {
        newFrames[f].roll1 = String(pin);
        if (pin === 10) {
          if (f >= 2 && newFrames[f-1].roll1 === "10" && newFrames[f-2].roll1 === "10") {
            confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 } });
          }
          newFrames[f].roll2 = ""; nf = f + 1; nr = 0;
        } else nr = 1;
      } else {
        const v1 = parseInt(newFrames[f].roll1, 10) || 0;
        if (v1 + pin <= 10) { newFrames[f].roll2 = String(pin); nf = f + 1; nr = 0; }
      }
    } else {
      if (r === 0) { newFrames[f].roll1 = String(pin); nr = 1; }
      else if (r === 1) {
        newFrames[f].roll2 = String(pin);
        const v1 = parseInt(newFrames[f].roll1, 10) || 0;
        if (v1 === 10 || v1 + pin === 10) nr = 2;
      } else { newFrames[f].roll3 = String(pin); }
    }
    setFrames(newFrames);
    setCurrentPos([nf, nr]);
  };

  const submitGame = async () => {
    const total = runningTotals[9] || 0;
    setIsSaving(true);
    try {
      await addDoc(collection(firestore, "games"), { 
        userId: user.uid, 
        userName: user.displayName, 
        photoURL: user.photoURL, 
        frames, 
        totalScore: total, 
        createdAt: timestamp() 
      });
      // Instant Reset
      setFrames(Array.from({ length: 10 }, () => ({ roll1: "", roll2: "", roll3: "" })));
      setCurrentPos([0, 0]);
    } catch (err) { 
      console.error(err); 
    } finally {
      setTimeout(() => setIsSaving(false), 1000); // Visual feedback duration
    }
  };

  const displayRoll = (f, r) => {
    const val = frames[f][`roll${r + 1}`];
    if (val === "") return "";
    const v = parseInt(val, 10);
    const first = parseInt(frames[f].roll1, 10) || 0;
    if (f < 9) {
      if (r === 0) return v === 10 ? "X" : val;
      return first + v === 10 ? "/" : val;
    }
    if (r === 0) return v === 10 ? "X" : val;
    if (r === 1) {
      if (v === 10) return "X";
      if (first < 10 && first + v === 10) return "/";
      return val;
    }
    if (r === 2) {
      const second = parseInt(frames[f].roll2, 10) || 0;
      if (v === 10) return "X";
      if (second < 10 && second + v === 10) return "/";
      return val;
    }
    return "";
  };

  return (
    <div className="si-container">
      <h2 className="neon-title">{isSaving ? "SAVING..." : "Live Scorecard"}</h2>

      <div className="si-pin-grid">
        {Array.from({ length: 11 }, (_, i) => (
          <button key={i} className="si-pin-btn" onClick={() => handlePinClick(i)}>{i}</button>
        ))}
      </div>

      <div className="si-card-area">
        <div className="si-frames-grid">
          {frames.map((frame, i) => {
            const shouldShowScore = frame.roll1 !== "" || (i === 9 && (frame.roll1 !== "" || frame.roll2 !== ""));
            return (
              <div key={i} className={`si-frame-box ${i === currentPos[0] ? 'si-active' : ''}`} onClick={() => setCurrentPos([i, 0])}>
                <div className="si-frame-num">{i + 1}</div>
                <div className="si-rolls">
                  <div className={`si-roll ${displayRoll(i, 0) === 'X' ? 'gold' : ''}`}>{displayRoll(i, 0)}</div>
                  <div className={`si-roll ${displayRoll(i, 1) === '/' || displayRoll(i, 1) === 'X' ? 'gold' : ''}`}>{displayRoll(i, 1)}</div>
                  {i === 9 && <div className={`si-roll ${displayRoll(i, 2) === 'X' || displayRoll(i, 2) === '/' ? 'gold' : ''}`}>{displayRoll(i, 2)}</div>}
                </div>
                <div className="si-total">{shouldShowScore ? runningTotals[i] : ""}</div>
              </div>
            );
          })}
        </div>
      </div>

      <button onClick={submitGame} className="si-submit" disabled={isSaving}>
        {isSaving ? "SCORE SENT!" : "Submit Game"}
      </button>
    </div>
  );
}